/*
 * Order with Free Proof — controller for blocks/order-with-free-proof.liquid
 * (Dual-Path-PDP-Template KICKOFF 2026-10-02, rulings R1–R8).
 *
 * Deferred, no dependencies, idempotent (one binding per page however many blocks render).
 * Everything is delegated from `document`, so a block re-rendered by the theme editor or a
 * variant-change morph keeps working without re-initialisation.
 *
 * WHAT IT DOES
 *   1. Opens / closes the native <dialog> (showModal gives the focus trap, inert background
 *      and Esc-to-close; focus returns to the trigger on close).
 *   2. Logo option radios: "Upload Logo" reveals the file input; the other two hide and clear it.
 *   3. Validates before the NATIVE submit: quantity >= MOQ and a multiple of the increment
 *      (from snippets/moq-rules.liquid via data-moq-min / data-moq-inc), a file only when
 *      "Upload Logo" is chosen, file <= 20 MB, an allowed type, never HTML.
 *   4. Keeps the posted variant id on the shopper's CURRENT selection, using the same
 *      mechanism as blocks/zakeke-design-now.liquid: the theme's ProductSelectEvent, whose
 *      promise resolves with the freshly rendered section HTML (assets/variant-picker.js).
 *      Bound by literal event name (no module import — see the D1 note at the binding), with a
 *      picker-JSON MutationObserver as an event-independent fallback.
 *
 * WHAT IT NEVER DOES
 *   - fetch / XHR. The form posts natively (multipart) to /cart/add and the browser follows the
 *     redirect to /cart. /cart/add.js cannot carry a file (REPORT-Hotfix-H2-2026-09-22), so it is
 *     not used even when no file is attached — one channel, nothing to drift.
 *   - disable controls to express validation. Refusals are messages beside the field.
 */
(function () {
  'use strict';

  if (window.orderWithFreeProofBound) return;
  window.orderWithFreeProofBound = true;

  var MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // Shopify platform file limit (MEMO-Logo-Upload-Native §2)
  var ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'ai', 'eps', 'svg']; // = MBE accept list
  var UPLOAD_OPTION = 'Upload Logo';
  var LOGO_FILE_PROPERTY = 'properties[_Logo file]';
  var LOGO_NAME_PROPERTY = 'properties[Your logo]';
  var NOTES_PROPERTY = 'properties[Artwork instructions]';

  // ---------------------------------------------------------------------------------------
  // Pure checks (also exposed on window.OrderWithFreeProof for the static harness)
  // ---------------------------------------------------------------------------------------
  function extensionOf(name) {
    var match = /\.([a-z0-9]+)$/i.exec(String(name || ''));
    return match ? match[1].toLowerCase() : '';
  }

  /** @returns {string} '' when the quantity is acceptable, else the message to show. */
  function quantityError(raw, min, inc) {
    min = Math.max(1, parseInt(min, 10) || 1);
    inc = Math.max(1, parseInt(inc, 10) || 1);
    var text = String(raw == null ? '' : raw).trim();
    if (!/^\d+$/.test(text)) return 'Enter a whole number — the minimum order is ' + min + '.';
    var n = parseInt(text, 10);
    if (n < min) return 'The minimum order for this product is ' + min + '.';
    if (n % inc !== 0) {
      var lower = Math.floor(n / inc) * inc;
      var upper = lower + inc;
      if (lower < min) return 'This product is sold in multiples of ' + inc + '. Try ' + upper + '.';
      return 'This product is sold in multiples of ' + inc + '. Try ' + lower + ' or ' + upper + '.';
    }
    return '';
  }

  /** @returns {string} '' when the file is acceptable, else the message to show. */
  function fileError(file) {
    if (!file) {
      return 'Choose a logo file to upload, or pick "Logo on File" or "Add Logo Later".';
    }
    var ext = extensionOf(file.name);
    if (ext === 'html' || ext === 'htm' || file.type === 'text/html') {
      return "HTML files can't be uploaded. Please choose a PNG, JPG, PDF, AI, EPS or SVG file.";
    }
    if (ALLOWED_EXTENSIONS.indexOf(ext) === -1) {
      return 'Please choose a PNG, JPG, PDF, AI, EPS or SVG file.';
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      var mb = (file.size / 1024 / 1024).toFixed(1);
      return (
        'That file is ' + mb + ' MB. The maximum is 20 MB — please choose a smaller file, ' +
        'or pick "Add Logo Later" and we will email you for it.'
      );
    }
    return '';
  }

  // ---------------------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------------------
  function one(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function rootOf(el) {
    return el && el.closest ? el.closest('[data-owfp]') : null;
  }

  function setMessage(el, text) {
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  function setInvalid(input, invalid) {
    if (!input) return;
    if (invalid) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  function selectedLogoOption(root) {
    var checked = one(root, '[data-owfp-logo-option]:checked');
    return checked ? checked.value : '';
  }

  function uploadActive(root) {
    return selectedLogoOption(root) === UPLOAD_OPTION;
  }

  function chosenFile(root) {
    if (!uploadActive(root)) return null;
    var input = one(root, '[data-owfp-file]');
    return input && input.files && input.files.length ? input.files[0] : null;
  }

  // ---------------------------------------------------------------------------------------
  // Logo option <-> file input
  // ---------------------------------------------------------------------------------------
  // Both the file input and the visible-name input ship WITHOUT a `name`. They are named only
  // while "Upload Logo" is chosen AND a file is picked: an unnamed input is not serialised, so
  // an empty file part (H1's "[object Object]") or a name with no file can never reach the line.
  function syncUpload(root) {
    var wrap = one(root, '[data-owfp-upload]');
    var input = one(root, '[data-owfp-file]');
    var nameInput = one(root, '[data-owfp-logo-name]');
    var active = uploadActive(root);

    if (wrap) wrap.hidden = !active;
    if (!active && input) {
      if (input.value) {
        try {
          input.value = '';
        } catch (error) {
          // Old engines refuse programmatic clears; the name removal below still drops it.
        }
      }
      setMessage(one(root, '[data-owfp-file-msg]'), '');
      setInvalid(input, false);
    }

    var file = chosenFile(root);
    if (input) {
      if (file) input.setAttribute('name', LOGO_FILE_PROPERTY);
      else input.removeAttribute('name');
    }
    if (nameInput) {
      if (file) {
        nameInput.value = file.name;
        nameInput.setAttribute('name', LOGO_NAME_PROPERTY);
      } else {
        nameInput.value = '';
        nameInput.removeAttribute('name');
      }
    }
  }

  // ---------------------------------------------------------------------------------------
  // Validation — returns the first control that failed, or null
  // ---------------------------------------------------------------------------------------
  function validateQuantity(root) {
    var input = one(root, '[data-owfp-qty]');
    if (!input) return null;
    var message = quantityError(input.value, root.dataset.moqMin, root.dataset.moqInc);
    setMessage(one(root, '[data-owfp-qty-msg]'), message);
    setInvalid(input, !!message);
    return message ? input : null;
  }

  function validateFile(root) {
    var input = one(root, '[data-owfp-file]');
    if (!uploadActive(root)) {
      setMessage(one(root, '[data-owfp-file-msg]'), '');
      setInvalid(input, false);
      return null;
    }
    var message = fileError(chosenFile(root));
    setMessage(one(root, '[data-owfp-file-msg]'), message);
    setInvalid(input, !!message);
    return message ? input : null;
  }

  function validate(root) {
    var firstBad = null;
    var qty = validateQuantity(root);
    var file = validateFile(root);
    firstBad = qty || file;
    return firstBad;
  }

  // ---------------------------------------------------------------------------------------
  // Variant
  // ---------------------------------------------------------------------------------------
  // Horizon's <variant-picker> keeps the selected variant serialised in a JSON script tag that
  // is morphed on every option change (the same source blocks/zakeke-design-now.liquid reads).
  // Scoped to this block's section so a quick-add picker elsewhere on the page is never read.
  function pickerVariant(root) {
    var scope = root.closest('.shopify-section') || document;
    var script = scope.querySelector('variant-picker script[type="application/json"]');
    if (!script) return undefined; // no picker at all (single-variant product)
    try {
      return JSON.parse(script.textContent); // may be null for a non-existent combination
    } catch (error) {
      return undefined;
    }
  }

  // { id, title, available } of the variant the CHECKED option inputs name together, or null
  // (DA1, 2026-10-15 — same rule as assets/mbe-buybox-v2.js checkedSelection). Each input of
  // Horizon's picker (snippets/variant-main-picker.liquid) carries `data-variant-id` = Liquid's
  // `product_option_value.variant`: the variant with THAT value and the other options as they
  // were when the picker was rendered. A click flips `checked` at once; the JSON island above
  // only follows when the section fetch lands. One option group: the checked input's id is that
  // value's variant. Several: trusted only when every checked input names the same id (that
  // variant then has every checked value); any disagreement → null → the JSON, as before.
  // Only the template's own product, numeric ids, no input linked to another product.
  // `available` is the inputs' data-option-available (null when any input does not say).
  function checkedSelection(root) {
    var scope = root.closest('.shopify-section') || document;
    var script = scope.querySelector('variant-picker script[type="application/json"]');
    var picker = script ? script.closest('variant-picker') : null;
    if (!picker || picker.getAttribute('data-template-product-match') !== 'true') return null;
    var options = Array.prototype.slice.call(picker.querySelectorAll('fieldset input:checked'));
    picker.querySelectorAll('select').forEach(function (select) {
      var option = select.options[select.selectedIndex];
      if (option) options.push(option);
    });
    if (!options.length) return null;
    var own = (picker.getAttribute('data-product-url') || '').split('?')[0];
    var id = null;
    var available = true;
    for (var i = 0; i < options.length; i++) {
      var vid = options[i].getAttribute('data-variant-id') || '';
      if (!/^\d+$/.test(vid)) return null;
      var linked = (options[i].getAttribute('data-connected-product-url') || '').split('?')[0];
      if (linked && linked !== own) return null;
      if (id === null) id = vid;
      else if (vid !== id) return null;
      var flag = options[i].getAttribute('data-option-available');
      if (flag === 'false') available = false;
      else if (flag !== 'true' && available === true) available = null;
    }
    return {
      id: id,
      title: options.map(function (option) { return option.value; }).join(' / '),
      available: available,
    };
  }

  function formatMoney(root, cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: root.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch (error) {
      return '';
    }
  }

  function setUnavailable(root, label) {
    var trigger = one(root, '[data-owfp-open]');
    var submit = one(root, '[data-owfp-submit]');
    [trigger, submit].forEach(function (button) {
      if (!button) return;
      button.disabled = true;
      button.dataset.soldOut = 'true';
      button.textContent = label || 'Unavailable';
    });
  }

  // The inverse, for the picker-JSON fallback below: a variant the picker reports as available
  // re-enables both buttons with the label the Liquid rendered for the available state
  // (data-available-label). The event path never needs this — it lifts fresh buttons instead.
  function setAvailable(root) {
    [
      [one(root, '[data-owfp-open]'), 'Order with Free Proof'],
      [one(root, '[data-owfp-submit]'), 'Add to cart'],
    ].forEach(function (pair) {
      var button = pair[0];
      if (!button || button.dataset.soldOut !== 'true') return;
      button.disabled = false;
      button.dataset.soldOut = 'false';
      button.textContent = button.dataset.availableLabel || pair[1];
    });
  }

  // Belt and braces under the morph below: immediately before opening and before submitting,
  // make the hidden id agree with the picker's current selection. The morph normally makes
  // this a no-op; it matters only if a morph was missed (e.g. an aborted section fetch).
  function syncVariantFromPicker(root) {
    var idInput = one(root, '[data-owfp-variant-input]');
    if (!idInput) return true;
    var variant = pickerVariant(root);
    // DA1: the checked inputs are ahead of the JSON right after a swatch click. Move the id and
    // name now; the price is not on the inputs, so it is refreshed once the JSON (or the event's
    // fresh HTML) catches up — `owfpAhead` makes the JSON path below rewrite it even though the
    // id then already matches.
    var checked = checkedSelection(root);
    if (checked && !(variant && String(variant.id) === checked.id)) {
      if (checked.id !== String(idInput.value)) {
        idInput.value = checked.id;
        var aheadName = one(root, '[data-owfp-variant-name]');
        if (aheadName && checked.title) aheadName.textContent = checked.title;
        root.dataset.owfpAhead = '1';
      }
      if (checked.available === false) {
        setUnavailable(root, 'Sold out');
        return false;
      }
      if (checked.available === true) setAvailable(root);
      return true;
    }
    if (variant === undefined) {
      var fromUrl = null;
      try {
        fromUrl = new URL(window.location.href).searchParams.get('variant');
      } catch (error) {
        fromUrl = null;
      }
      if (fromUrl && /^\d+$/.test(fromUrl) && fromUrl !== idInput.value && one(document, 'variant-picker')) {
        idInput.value = fromUrl;
      }
      return true;
    }
    if (variant === null || !variant.id) {
      setUnavailable(root, 'Unavailable');
      return false;
    }
    if (String(variant.id) !== String(idInput.value) || root.dataset.owfpAhead === '1') {
      delete root.dataset.owfpAhead;
      idInput.value = String(variant.id);
      var name = one(root, '[data-owfp-variant-name]');
      if (name && variant.title && variant.title !== 'Default Title') name.textContent = variant.title;
      var price = one(root, '[data-owfp-variant-price]');
      if (price && typeof variant.price === 'number') {
        var formatted = formatMoney(root, variant.price);
        if (formatted) price.textContent = formatted;
      }
    }
    if (variant.available === false) {
      setUnavailable(root, 'Sold out');
      return false;
    }
    setAvailable(root);
    return true;
  }

  // The primary mechanism (mirrors blocks/zakeke-design-now.liquid and blocks/pricing-tiers):
  // lift this block's freshly Liquid-rendered, variant-dependent parts out of the section HTML
  // the ProductSelectEvent promise resolves with. Availability, price and the MOQ are never
  // recomputed in JavaScript, so this block and Design Now cannot drift apart.
  function applyVariantHtml(root, doc) {
    var fresh = doc.querySelector('[data-owfp][data-block-id="' + root.dataset.blockId + '"]');
    if (!fresh) return;

    ['[data-owfp-open]', '[data-owfp-variant]', '[data-owfp-submit]', '[data-owfp-qty-hint]'].forEach(
      function (selector) {
        var live = one(root, selector);
        var next = one(fresh, selector);
        if (live && next) live.replaceWith(next.cloneNode(true));
      }
    );

    var liveId = one(root, '[data-owfp-variant-input]');
    var freshId = one(fresh, '[data-owfp-variant-input]');
    if (liveId && freshId) liveId.value = freshId.value;

    root.dataset.moqMin = fresh.dataset.moqMin || root.dataset.moqMin;
    root.dataset.moqInc = fresh.dataset.moqInc || root.dataset.moqInc;
    var qty = one(root, '[data-owfp-qty]');
    if (qty) {
      qty.min = root.dataset.moqMin;
      qty.step = root.dataset.moqInc;
      if ((parseInt(qty.value, 10) || 0) < (parseInt(root.dataset.moqMin, 10) || 1)) {
        qty.value = root.dataset.moqMin;
      }
    }
  }

  function bindVariantEvents(eventName) {
    document.addEventListener(eventName, function (event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('product-card')) return;
      var section = event.target.closest('.shopify-section, dialog') || document;
      var roots = section.querySelectorAll('[data-owfp][data-block-id]');
      if (!roots.length || !event.promise) return;
      event.promise
        .then(function (result) {
          var doc = result && result.detail && result.detail.html;
          if (!doc) return;
          roots.forEach(function (root) {
            applyVariantHtml(root, doc);
            // A non-existent option combination resolves with no variant resource.
            if (result.detail && 'resource' in result.detail && result.detail.resource === null) {
              setUnavailable(root, 'Unavailable');
            }
          });
        })
        .catch(function () {});
    });
  }

  // Bound by LITERAL name, never resolved through a dynamic module import of '@shopify/events'.
  // Shopify's theme-asset minifier rewrites a dynamic import in an asset file into
  // `require("@shopify/events")`, which throws in the browser — so the old binding silently fell
  // back to an event Horizon never fires and the morph never ran on the CDN (TEST-Live-DraftTheme
  // 2026-10-02, D1). `shopify:product:select` is StandardEvents.productSelect
  // (assets/standard-events.d.ts) — the event assets/variant-picker.js dispatches, verified live.
  // `variant:update` is kept as a harmless alias. Inline <script>s (blocks/zakeke-design-now)
  // are not minified, which is why Design Now was unaffected.
  ['shopify:product:select', 'variant:update'].forEach(bindVariantEvents);

  // Fallback that depends on no event name at all. Horizon morphs the picker's JSON script (the
  // selected variant) on every option change, BEFORE the event promise resolves. Watching for that
  // morph re-syncs the hidden id, variant name, price and sold-out state straight away, so the
  // modal is right before it is opened even if the event above is ever renamed. (A per-variant
  // MOQ change still needs the Liquid render, i.e. the event path.)
  var pickerSyncQueued = false;

  function queuePickerSync() {
    if (pickerSyncQueued) return;
    pickerSyncQueued = true;
    setTimeout(function () {
      pickerSyncQueued = false;
      document.querySelectorAll('[data-owfp][data-block-id]').forEach(function (root) {
        syncVariantFromPicker(root);
      });
    }, 0);
  }

  if (typeof MutationObserver === 'function') {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var node = records[i].target;
        if (node && node.nodeType !== 1) node = node.parentNode;
        if (node && node.closest && node.closest('variant-picker')) {
          queuePickerSync();
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, characterData: true, subtree: true });
  }
  // History navigation within the PDP (the picker writes ?variant= with replaceState).
  window.addEventListener('popstate', queuePickerSync);

  // ---------------------------------------------------------------------------------------
  // Dialog
  // ---------------------------------------------------------------------------------------
  function resetBusy(root) {
    var submit = one(root, '[data-owfp-submit]');
    var form = one(root, '[data-owfp-form]');
    if (form) delete form.dataset.busy;
    if (submit && submit.getAttribute('aria-busy') === 'true') {
      submit.removeAttribute('aria-busy');
      if (submit.dataset.idleLabel) submit.textContent = submit.dataset.idleLabel;
    }
    var notes = one(root, '[data-owfp-notes]');
    if (notes) notes.setAttribute('name', NOTES_PROPERTY);
  }

  function openDialog(root, trigger) {
    var dialog = one(root, '[data-owfp-dialog]');
    if (!dialog || dialog.open) return;
    if (!syncVariantFromPicker(root)) return;
    resetBusy(root);
    syncUpload(root);
    root.owfpTrigger = trigger;
    // Horizon's own page scroll lock (assets/base.css `html[scroll-lock]`), removed on close.
    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      dialog.dataset.owfpLocked = '1';
    }
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    var close = one(dialog, '[data-owfp-close]');
    if (close) close.focus();
  }

  // Idempotent clean-up after the dialog has closed by ANY route (close button, backdrop, Esc,
  // a script calling close()). Runs synchronously from closeDialog and again from the `cancel` /
  // `close` events, so it never depends on the `close` event alone being delivered.
  function afterClose(dialog) {
    if (!dialog || dialog.open) return;
    if (dialog.dataset.owfpLocked === '1') {
      document.documentElement.removeAttribute('scroll-lock');
      delete dialog.dataset.owfpLocked;
    }
    var root = rootOf(dialog);
    var trigger = (root && root.owfpTrigger) || one(root, '[data-owfp-open]');
    if (root) root.owfpTrigger = null;
    if (trigger && trigger.isConnected && !trigger.disabled && document.activeElement !== trigger) {
      trigger.focus();
    }
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    afterClose(dialog);
  }

  var pressStartedOnBackdrop = false;

  document.addEventListener('pointerdown', function (event) {
    pressStartedOnBackdrop = !!(event.target && event.target.matches && event.target.matches('[data-owfp-dialog]'));
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var opener = target.closest('[data-owfp-open]');
    if (opener) {
      if (opener.disabled || opener.dataset.soldOut === 'true') return;
      event.preventDefault();
      openDialog(rootOf(opener), opener);
      return;
    }

    var closer = target.closest('[data-owfp-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('[data-owfp-dialog]'));
      return;
    }

    // Backdrop click: the panel fills the dialog, so the dialog itself is only ever the
    // target when the press landed on the backdrop. A drag that STARTED inside the panel and
    // ended outside also targets the dialog — pressStartedOnBackdrop keeps that open.
    if (target.matches('[data-owfp-dialog]') && pressStartedOnBackdrop) {
      closeDialog(target);
    }
  });

  // `cancel` (Esc) and `close` do not bubble — capture them. `cancel` can be prevented, so its
  // clean-up waits a task and only runs if the dialog really closed.
  function onDialogEvent(event) {
    var dialog = event.target;
    if (!(dialog instanceof Element) || !dialog.matches('[data-owfp-dialog]')) return;
    if (event.type === 'cancel') {
      setTimeout(function () {
        afterClose(dialog);
      }, 0);
    } else {
      afterClose(dialog);
    }
  }
  document.addEventListener('close', onDialogEvent, true);
  document.addEventListener('cancel', onDialogEvent, true);

  // ---------------------------------------------------------------------------------------
  // Field reactions
  // ---------------------------------------------------------------------------------------
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var root = rootOf(target);
    if (!root) return;

    if (target.matches('[data-owfp-logo-option]')) {
      syncUpload(root);
      if (!uploadActive(root)) validateFile(root);
      else setMessage(one(root, '[data-owfp-file-msg]'), '');
    } else if (target.matches('[data-owfp-file]')) {
      syncUpload(root);
      if (chosenFile(root)) validateFile(root);
    } else if (target.matches('[data-owfp-qty]')) {
      validateQuantity(root);
    }
  });

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (!(target instanceof Element) || !target.matches('[data-owfp-qty]')) return;
    var root = rootOf(target);
    setMessage(one(root, '[data-owfp-qty-msg]'), '');
    setInvalid(target, false);
  });

  // ---------------------------------------------------------------------------------------
  // Submit — validate, tidy the payload, then let the NATIVE multipart POST proceed
  // ---------------------------------------------------------------------------------------
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-owfp-form]')) return;
    var root = rootOf(form);
    if (!root) return;

    // A second press while the first POST is uploading sends nothing.
    if (form.dataset.busy === '1') {
      event.preventDefault();
      return;
    }

    if (!syncVariantFromPicker(root)) {
      event.preventDefault();
      return;
    }
    syncUpload(root);

    var bad = validate(root);
    if (bad) {
      event.preventDefault();
      if (bad.closest('[hidden]')) bad = one(root, '[data-owfp-logo-option]:checked') || bad;
      bad.focus();
      return;
    }

    // An empty notes box posts nothing rather than a blank property. Restored by resetBusy.
    var notes = one(root, '[data-owfp-notes]');
    if (notes) {
      if (notes.value.trim()) notes.setAttribute('name', NOTES_PROPERTY);
      else notes.removeAttribute('name');
    }

    form.dataset.busy = '1';
    var submit = one(root, '[data-owfp-submit]');
    if (submit) {
      submit.dataset.idleLabel = submit.textContent.trim();
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = chosenFile(root) ? 'Uploading your logo…' : 'Adding to cart…';
    }
    // No preventDefault: the browser performs the multipart POST to /cart/add and follows the
    // redirect to /cart.
  });

  // Back-button restores from the bfcache keep the busy state; clear it.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    document.querySelectorAll('[data-owfp]').forEach(resetBusy);
  });

  window.OrderWithFreeProof = {
    quantityError: quantityError,
    fileError: fileError,
    extensionOf: extensionOf,
    MAX_UPLOAD_BYTES: MAX_UPLOAD_BYTES,
  };
})();
